// Weather: METAR-equivalent state, wind aloft, runway configuration choice, transition level and the ATIS.
import type { AirportMeta, RunwayConfigMeta } from '../data/types';
import { Rng } from './rng';
import { angDiff, DEG } from './geo';
import { sayWind, spell, sayFeet, ALPHABET, sayRunway } from '../phraseology/spoken';

export type CloudCover = 'FEW' | 'SCT' | 'BKN' | 'OVC';
export interface Weather {
  /** Surface wind, true direction the wind blows from. */
  windDir: number;
  windKt: number;
  gustKt?: number;
  variable?: boolean;
  visM: number;
  rvrM?: number;
  clouds: { cover: CloudCover; baseFt: number; cb?: boolean }[];
  tempC: number;
  dewC: number;
  qnh: number;
  /** Present weather codes, e.g. '-RA', 'BR', 'FG', 'TSRA', 'SN'. */
  wx: string[];
  trend: 'NOSIG' | 'BECMG' | 'TEMPO';
}

export type WeatherPreset = 'cavok' | 'good' | 'crosswind' | 'rain' | 'lowvis' | 'fog' | 'storm' | 'snow' | 'random';

export function makeWeather(meta: AirportMeta, preset: WeatherPreset, rng: Rng, forceConfig?: string): Weather {
  const p = preset === 'random' ? rng.weighted(['good', 'good', 'cavok', 'rain', 'crosswind'] as const, () => 1) : preset;
  const cfg = forceConfig ? meta.configs.find(c => c.id === forceConfig) : rng.weighted(meta.configs, c => c.share);
  const base = cfg?.windDir ?? 270;
  const w: Weather = { windDir: 0, windKt: 0, visM: 9999, clouds: [], tempC: 12, dewC: 7, qnh: 1015, wx: [], trend: 'NOSIG' };
  w.windDir = Math.round((base + rng.range(-30, 30) + 360) % 360 / 10) * 10 || 360;
  w.windKt = Math.round(rng.range(4, 12));
  w.tempC = Math.round(rng.range(4, 22));
  w.dewC = w.tempC - Math.round(rng.range(2, 8));
  w.qnh = Math.round(rng.range(1003, 1027));
  switch (p) {
    case 'cavok': w.windKt = Math.round(rng.range(2, 8)); break;
    case 'good': w.clouds = [{ cover: rng.pick(['FEW', 'SCT'] as const), baseFt: rng.int(25, 45) * 100 }]; break;
    case 'crosswind':
      w.windDir = Math.round(((base + rng.pick([-1, 1]) * rng.range(55, 80)) + 360) % 360 / 10) * 10 || 360;
      w.windKt = Math.round(rng.range(16, 24)); w.gustKt = w.windKt + Math.round(rng.range(8, 14));
      w.clouds = [{ cover: 'SCT', baseFt: rng.int(20, 35) * 100 }, { cover: 'BKN', baseFt: rng.int(45, 70) * 100 }];
      break;
    case 'rain':
      w.visM = rng.int(4, 8) * 1000; w.wx = ['-RA'];
      w.clouds = [{ cover: 'BKN', baseFt: rng.int(10, 18) * 100 }, { cover: 'OVC', baseFt: rng.int(30, 50) * 100 }];
      w.dewC = w.tempC - 1;
      break;
    case 'lowvis':
      w.visM = rng.int(4, 9) * 100; w.rvrM = rng.int(5, 9) * 100; w.wx = ['BR'];
      w.clouds = [{ cover: 'OVC', baseFt: rng.int(2, 4) * 100 }]; w.windKt = Math.round(rng.range(2, 6)); w.dewC = w.tempC;
      break;
    case 'fog':
      w.visM = rng.int(1, 3) * 100; w.rvrM = rng.int(2, 5) * 100; w.wx = ['FG'];
      w.clouds = [{ cover: 'OVC', baseFt: 100 }]; w.windKt = Math.round(rng.range(0, 4)); w.tempC = rng.int(-2, 8); w.dewC = w.tempC;
      break;
    case 'storm':
      w.windKt = Math.round(rng.range(14, 22)); w.gustKt = w.windKt + Math.round(rng.range(12, 20));
      w.visM = rng.int(3, 7) * 1000; w.wx = ['TSRA']; w.tempC = rng.int(18, 28); w.dewC = w.tempC - 3;
      w.clouds = [{ cover: 'SCT', baseFt: rng.int(25, 40) * 100, cb: true }, { cover: 'BKN', baseFt: rng.int(60, 80) * 100 }];
      w.trend = 'TEMPO';
      break;
    case 'snow':
      w.visM = rng.int(12, 30) * 100; w.wx = ['SN']; w.tempC = rng.int(-6, 0); w.dewC = w.tempC - 1;
      w.clouds = [{ cover: 'OVC', baseFt: rng.int(6, 12) * 100 }];
      break;
  }
  return w;
}

export function isCavok(w: Weather): boolean {
  return w.visM >= 9999 && !w.wx.length && w.clouds.every(c => c.baseFt >= 5000 && !c.cb);
}
export function ceilingFt(w: Weather): number {
  return w.clouds.filter(c => c.cover === 'BKN' || c.cover === 'OVC').reduce((m, c) => Math.min(m, c.baseFt), 99999);
}
/** Low visibility procedures: RVR below 550 m or ceiling at or below 200 ft (CAT II/III). */
export function lvpActive(w: Weather): boolean {
  return (w.rvrM ?? w.visM) < 600 || ceilingFt(w) <= 200;
}

/** Headwind and crosswind components (kt) for a runway with true heading hdg. */
export function windComponents(w: Weather, hdgTrue: number): { head: number; cross: number } {
  const a = angDiff(hdgTrue, w.windDir) * DEG;
  return { head: w.windKt * Math.cos(a), cross: w.windKt * Math.sin(a) };
}

/** Configuration chosen by the supervisor: preferred direction unless the tailwind exceeds 5 kt. */
export function chooseConfig(meta: AirportMeta, w: Weather, runwayHdg: (end: string) => number): RunwayConfigMeta {
  const sorted = [...meta.configs].sort((a, b) => b.share - a.share);
  for (const c of sorted) {
    const worst = Math.min(...c.arr.map(e => windComponents(w, runwayHdg(e)).head - (w.gustKt ? (w.gustKt - w.windKt) / 2 : 0)));
    if (worst >= -5) return c;
  }
  return sorted[0]!;
}

/** Transition level for TA 5000 ft (AIP Germany ENR 1.7): depends on QNH. */
export function transitionLevel(meta: AirportMeta, qnh: number): number {
  if (meta.transitionAltFt === 5000) return qnh >= 1032 ? 60 : qnh >= 1013 ? 70 : qnh >= 978 ? 80 : 90;
  return Math.ceil((meta.transitionAltFt + 1000 + (1013.25 - qnh) * 27.3) / 1000) * 10;
}

/** Wind at altitude: veers and strengthens with height (simple boundary layer + gradient wind). */
export function windAt(w: Weather, altFt: number): { dir: number; kt: number } {
  const k = Math.min(1, altFt / 3000);
  const kt = w.windKt * (1 + 1.2 * k) + Math.max(0, altFt - 3000) / 1000 * 2.2;
  return { dir: (w.windDir + 25 * k + Math.max(0, altFt - 3000) / 1000 * 1.5) % 360, kt: Math.min(kt, 140) };
}

// ------------------------------------------------------------------ METAR and ATIS
const pad = (n: number, l = 2) => String(Math.round(Math.abs(n))).padStart(l, '0');
const tempStr = (t: number) => (t < 0 ? 'M' : '') + pad(t);
/** German METARs are issued at :20 and :50 – the report valid at a given time. */
export function reportTime(utc: Date): Date {
  const d = new Date(utc.getTime());
  const m = d.getUTCMinutes();
  if (m >= 50) d.setUTCMinutes(50, 0, 0);
  else if (m >= 20) d.setUTCMinutes(20, 0, 0);
  else { d.setUTCHours(d.getUTCHours() - 1); d.setUTCMinutes(50, 0, 0); }
  return d;
}

export function metar(icao: string, w: Weather, utcNow: Date): string {
  const utc = reportTime(utcNow);
  const t = `${pad(utc.getUTCDate())}${pad(utc.getUTCHours())}${pad(utc.getUTCMinutes())}Z`;
  const wind = w.windKt < 1 ? '00000KT' : `${w.variable ? 'VRB' : pad(w.windDir, 3)}${pad(w.windKt)}${w.gustKt ? 'G' + pad(w.gustKt) : ''}KT`;
  const parts = [icao, t, wind];
  if (isCavok(w)) parts.push('CAVOK');
  else {
    parts.push(w.visM >= 9999 ? '9999' : pad(w.visM, 4));
    if (w.rvrM) parts.push(`R${'XX'}/${pad(w.rvrM, 4)}`);
    parts.push(...w.wx);
    parts.push(...(w.clouds.length ? w.clouds.map(c => `${c.cover}${pad(c.baseFt / 100, 3)}${c.cb ? 'CB' : ''}`) : ['NSC']));
  }
  parts.push(`${tempStr(w.tempC)}/${tempStr(w.dewC)}`, `Q${w.qnh}`, w.trend);
  return parts.join(' ');
}

const COVER_SAY: Record<CloudCover, string> = { FEW: 'few', SCT: 'scattered', BKN: 'broken', OVC: 'overcast' };
const WX_SAY: Record<string, string> = { '-RA': 'light rain', RA: 'rain', BR: 'mist', FG: 'fog', TSRA: 'thunderstorm with rain', SN: 'snow', '-SN': 'light snow' };

export interface AtisInfo { letter: string; text: string; spoken: string }

export function atis(meta: AirportMeta, w: Weather, cfg: RunwayConfigMeta, letter: string, utcNow: Date, tl: number, lvp: boolean): AtisInfo {
  const utc = reportTime(utcNow);
  const time = `${pad(utc.getUTCHours())}${pad(utc.getUTCMinutes())}`;
  const arr = cfg.arr.join(' and '), dep = cfg.dep.join(' and ');
  const same = arr === dep;
  const vis = w.visM >= 9999 ? 'visibility 10 kilometers or more' : w.visM >= 5000 ? `visibility ${Math.round(w.visM / 1000)} kilometers` : `visibility ${w.visM} meters`;
  const clouds = isCavok(w) ? 'CAVOK' : w.clouds.length ? w.clouds.map(c => `${COVER_SAY[c.cover]}${c.cb ? ' cumulonimbus' : ''} ${c.baseFt} feet`).join(', ') : 'no significant clouds';
  const textParts = [
    `${meta.say} Information ${letter}`, `met report time ${time}`,
    `expect ILS approach`, same ? `runway in use ${arr}` : `arrival runway ${arr}, departure runway ${dep}`,
    lvp ? 'low visibility procedures in operation' : '',
    `transition level ${tl}`,
    w.windKt < 1 ? 'wind calm' : `wind ${pad(w.windDir, 3)} degrees ${w.windKt} knots${w.gustKt ? ` gusts ${w.gustKt} knots` : ''}`,
    isCavok(w) ? 'CAVOK' : vis,
    w.rvrM ? `RVR ${w.rvrM} meters` : '',
    ...w.wx.map(x => WX_SAY[x] ?? x),
    isCavok(w) ? '' : clouds,
    `temperature ${w.tempC}`, `dew point ${w.dewC}`, `QNH ${w.qnh}`,
    w.trend === 'NOSIG' ? 'trend nosig' : w.trend === 'TEMPO' ? 'trend temporary thunderstorms' : 'trend becoming',
    `${meta.say} Information ${letter} out`,
  ].filter(Boolean);
  const spokenParts = [
    `${meta.say} Information ${ALPHABET[letter]}`, `met report time ${spell(time)}`,
    'expect ILS approach', same ? `runway in use ${cfg.arr.map(sayRunway).join(' and ')}` : `arrival runway ${cfg.arr.map(sayRunway).join(' and ')}, departure runway ${cfg.dep.map(sayRunway).join(' and ')}`,
    lvp ? 'low visibility procedures in operation' : '',
    `transition level ${spell(String(tl))}`,
    sayWind(w.windDir, w.windKt, w.gustKt),
    isCavok(w) ? 'cavok' : w.visM >= 9999 ? 'visibility one zero kilometers or more' : w.visM >= 5000 ? `visibility ${spell(String(Math.round(w.visM / 1000)))} kilometers` : `visibility ${spell(String(w.visM))} meters`,
    w.rvrM ? `R V R ${spell(String(w.rvrM))} meters` : '',
    ...w.wx.map(x => WX_SAY[x] ?? x),
    isCavok(w) ? '' : w.clouds.length ? w.clouds.map(c => `${COVER_SAY[c.cover]}${c.cb ? ' cumulonimbus' : ''} ${sayFeet(c.baseFt)} feet`).join(', ') : 'no significant clouds',
    `temperature ${w.tempC < 0 ? 'minus ' : ''}${spell(String(Math.abs(w.tempC)))}`, `dew point ${w.dewC < 0 ? 'minus ' : ''}${spell(String(Math.abs(w.dewC)))}`,
    `Q N H ${spell(String(w.qnh))}`,
    w.trend === 'NOSIG' ? 'trend nosig' : w.trend === 'TEMPO' ? 'trend temporary thunderstorms' : 'trend becoming',
    `${meta.say} Information ${ALPHABET[letter]} out`,
  ].filter(Boolean);
  return { letter, text: textParts.join(', '), spoken: spokenParts.join(', ') };
}

export const nextAtisLetter = (l: string): string => String.fromCharCode(((l.charCodeAt(0) - 65 + 1) % 26) + 65);
